<template>
  <div class="p-4">

    <!-- Selector de empresa (solo si hay contratos activos en más de una) -->
    <div v-if="companyOptions.length > 1" class="mb-4" style="max-width: 320px;">
      <label class="wz-form-label">Empresa</label>
      <wz-select v-model="selectedCompanyId" :items="companyOptions" @change="onCompanyChange" />
    </div>

    <!-- Sin contrato vigente -->
    <wz-alert v-if="noActiveContract" variant="secondary" icon="ri-calendar-close-line" class="text-center">
      Este colaborador no tiene contrato vigente.
    </wz-alert>

    <div v-else-if="balance">

      <!-- Resumen -->
      <div class="flex flex-wrap mb-4 -mx-2">
        <div class="w-full md:w-1/4 px-2">
          <div class="stat-card p-3 border rounded text-center">
            <div class="text-muted text-sm">Empresa</div>
            <div class="font-bold">{{ balance.companyName }}</div>
          </div>
        </div>
        <div class="w-full md:w-1/4 px-2">
          <div class="stat-card p-3 border rounded text-center">
            <div class="text-muted text-sm">Régimen laboral</div>
            <div class="font-bold">{{ balance.laborRegime }}</div>
          </div>
        </div>
        <div class="w-full md:w-1/4 px-2">
          <div class="stat-card p-3 border rounded text-center">
            <div class="text-muted text-sm">Días pendientes</div>
            <div class="font-bold text-primary h4 mb-0">{{ balance.totalDaysPending }}</div>
          </div>
        </div>
        <div class="w-full md:w-1/4 px-2">
          <div class="stat-card p-3 border rounded text-center">
            <div class="text-muted text-sm">Días tomados</div>
            <div class="font-bold h4 mb-0">{{ balance.totalDaysTaken }}</div>
          </div>
        </div>
      </div>

      <!-- Alerta períodos por vencer -->
      <wz-alert v-if="balance.hasExpiringPeriods" variant="warning" icon="ri-alarm-warning-line" class="mb-4">
        Hay períodos vacaciones próximos a vencer. Se recomienda programar el goce vacacional.
      </wz-alert>

      <!-- Botón nueva solicitud -->
      <div class="flex justify-between items-center mb-3">
        <h5 class="mb-0">Períodos vacaciones</h5>
        <wz-button
          variant="primary"
          size="sm"
          @click="$router.push({ name: 'vacation-requests-create', query: { employeeId } })"
        >
          <i class="ri-calendar-check-line mr-1"></i> Nueva solicitud
        </wz-button>
      </div>

      <!-- Tabla de períodos -->
      <div class="overflow-x-auto mb-4">
        <table class="wz-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Inicio período</th>
              <th>Fin período</th>
              <th>Vence</th>
              <th>Ganados</th>
              <th>Tomados</th>
              <th>Pendientes</th>
              <th>Estado</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="balance.periods.length === 0 && !balance.truncDays">
              <td colspan="8" class="text-center text-muted py-3">No hay períodos registrados</td>
            </tr>
            <tr v-for="(period, index) in sortedPeriods" :key="period.id"
                :class="{ 'text-muted': period.status === 'CANCELADO' }">
              <td>{{ index + 1 }}</td>
              <td v-local-date="period.startDate"></td>
              <td v-local-date="period.endDate"></td>
              <td v-local-date="period.expirationDate"></td>
              <td>{{ period.daysEarned }}</td>
              <td>{{ period.daysTaken }}</td>
              <td><strong>{{ period.daysPending }}</strong></td>
              <td>
                <wz-badge :variant="getPeriodStatusColor(period.status)">{{ getPeriodStatusLabel(period.status) }}</wz-badge>
              </td>
            </tr>
            <!-- Período trunco -->
            <tr v-if="balance.truncDays > 0" class="text-muted" style="font-style: italic;">
              <td>—</td>
              <td colspan="2">Período trunco (proporcional)</td>
              <td>—</td>
              <td>—</td>
              <td>—</td>
              <td>{{ balance.truncDays }}</td>
              <td><wz-badge variant="secondary-solid">TRUNCO</wz-badge></td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Tabla de solicitudes -->
      <h5 class="mb-3">Solicitudes de vacaciones</h5>
      <div class="overflow-x-auto">
        <table class="wz-table">
          <thead>
            <tr>
              <th>Fecha solicitud</th>
              <th>Fecha inicio</th>
              <th>Fecha fin</th>
              <th>Días</th>
              <th>Estado</th>
              <th class="text-center">Detalle</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="requests.length === 0">
              <td colspan="6" class="text-center text-muted py-3">No hay solicitudes registradas</td>
            </tr>
            <tr v-for="req in requests" :key="req.id">
              <td v-local-date="req.createdAt"></td>
              <td v-local-date="req.startDate"></td>
              <td v-local-date="req.endDate"></td>
              <td>{{ req.calendarDays }}</td>
              <td>
                <wz-badge :variant="getRequestStatusColor(req.status)">{{ req.status }}</wz-badge>
              </td>
              <td class="text-center">
                <router-link :to="{ name: 'vacation-requests-detail', params: { id: req.id },
                             query: { from: 'employee', employeeId } }" class="text-primary">
                  <i class="ri-eye-line"></i>
                </router-link>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

    </div>

    <!-- Loading inicial -->
    <div v-else class="text-center py-4 text-muted">
      <i class="ri-loader-4-line"></i> Cargando...
    </div>

  </div>
</template>

<script>
import { VacationService } from '@/core/services'
import { Constants } from '@/core/utils'

export default {
  name: 'VacationsTab',
  props: {
    employeeId: {
      type: Number,
      default: null,
    },
    // Contratos activos del colaborador (employee.currentContracts) para el selector de empresa.
    contracts: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      balance: null,
      requests: [],
      noActiveContract: false,
      selectedCompanyId: null,
    }
  },
  computed: {
    // Empresas distintas de los contratos activos.
    companyOptions() {
      const seen = new Set()
      const opts = []
      for (const c of (this.contracts || [])) {
        const id = (c.company && c.company.id) || c.companyId
        const name = (c.company && c.company.name) || c.companyName
        if (id != null && !seen.has(id)) {
          seen.add(id)
          opts.push({ key: id, label: name || `Empresa ${id}` })
        }
      }
      return opts
    },
    // Períodos con los CANCELADO (historia congelada por cambio de fecha de ingreso) al final.
    sortedPeriods() {
      const periods = (this.balance && this.balance.periods) || []
      return [...periods].sort((a, b) => {
        const ac = a.status === 'CANCELADO' ? 1 : 0
        const bc = b.status === 'CANCELADO' ? 1 : 0
        return ac - bc
      })
    },
  },
  watch: {
    // Inicializa/ajusta la empresa cuando llegan o cambian los contratos (carga async desde la ficha).
    contracts: {
      immediate: true,
      handler() {
        const ids = this.companyOptions.map(o => o.key)
        this.selectedCompanyId = ids.includes(this.selectedCompanyId)
          ? this.selectedCompanyId
          : (ids.length ? ids[0] : null)
        this.loadBalance()
      },
    },
  },
  methods: {
    onCompanyChange(companyId) {
      this.selectedCompanyId = companyId
      this.loadBalance()
    },
    getPeriodStatusColor(status) {
      const found = Object.values(Constants.VacationPeriodStatus).find(s => s.key === status)
      return found ? found.color : 'dark'
    },
    getPeriodStatusLabel(status) {
      const found = Object.values(Constants.VacationPeriodStatus).find(s => s.key === status)
      return found ? found.label : status
    },
    getRequestStatusColor(status) {
      const found = Object.values(Constants.VacationRequestStatus).find(s => s.key === status)
      return found ? found.color : 'dark'
    },
    async loadBalance() {
      if (!this.employeeId) return
      this.noActiveContract = false
      try {
        // Con empresa seleccionada → balance de esa empresa; si no, el endpoint por colaborador (compat).
        const resp = this.selectedCompanyId
          ? await VacationService.getBalanceByEmployeeCompany(this.employeeId, this.selectedCompanyId)
          : await VacationService.getBalance(this.employeeId)
        const payload = resp.payload
        if (!payload || !payload.contractId) {
          this.balance = null
          this.noActiveContract = true
        } else {
          this.balance = payload
        }
      } catch (e) {
        this.balance = null
        this.noActiveContract = true
      }
    },
    async loadRequests() {
      if (!this.employeeId) return
      try {
        const resp = await VacationService.getRequestsByEmployee(this.employeeId)
        this.requests = resp.payload || []
      } catch (e) {
        console.error(e)
      }
    },
  },
  mounted() {
    // El balance lo carga el watch de `contracts` (immediate). Acá solo las solicitudes.
    this.loadRequests()
  },
}
</script>
